<script setup lang="ts">
  import { useTranslation } from '@/composables/useTranslation'
  import { CheckCircle, Loader2 } from 'lucide-vue-next'
  import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

  const { t } = useTranslation({
    en: {
      AllDocumentsAnalyzed: 'All documents analyzed',
      DocumentsInAnalysis: '{count} Documents still in analysis',
      DocumentsBeingAnalyzed: 'Documents are being analyzed - AI may produce incorrect results',
      Processing: 'Processing',
      DocumentsReady: 'Documents ready for AI',
      DocumentsUploading: 'Documents uploading',
      Uploading: 'uploading',
      ProcessingDocs: 'processing',
      DocumentsBeingAnalyzedDataUpdate:
        '{count} documents are being analyzed - data will be updated soon'
    },
    de: {
      AllDocumentsAnalyzed: 'Alle Dokumente analysiert',
      DocumentsInAnalysis: '{count} Dokumente noch in Analyse',
      DocumentsBeingAnalyzed:
        'Dokumente werden analysiert – die KI kann inkorrekte Ergebnisse produzieren',
      Processing: 'Verarbeitung',
      DocumentsReady: 'Dokumente sind KI-bereit',
      DocumentsUploading: 'Dokumente werden hochgeladen',
      Uploading: 'werden hochgeladen',
      ProcessingDocs: 'werden verarbeitet',
      DocumentsBeingAnalyzedDataUpdate:
        '{count} Dokumente werden analysiert - Daten werden bald aktualisiert'
    }
  })

  // Props for customization and data
  const props = withDefaults(
    defineProps<{
      // Data props - pass the actual data instead of being smart
      documents?: any[]
      processingDocuments?: any[]
      activeUploads?: any[]
      documentsWithErrors?: any[]

      // UI customization props
      showOnlyWhenActive?: boolean // Only show status when there's activity
      autoHideSuccessAfter?: number // Auto-hide success message after X seconds (0 = no auto-hide)
      compact?: boolean // Compact version for smaller spaces
      customTemplate?: string // Custom template for status text (e.g., '{count} documents are being analyzed - data will be updated soon')
      customContainerClass?: string // Custom CSS classes for the container
      customIconClass?: string // Custom CSS classes for the icon
      customTextClass?: string // Custom CSS classes for the text
      hideIcon?: boolean // Hide the status icon
      hideBadges?: boolean // Hide the count badges
      useSlots?: boolean // Enable slot-based rendering for full customization
    }>(),
    {
      documents: () => [],
      processingDocuments: () => [],
      activeUploads: () => [],
      documentsWithErrors: () => [],
      showOnlyWhenActive: false,
      autoHideSuccessAfter: 3000,
      compact: false,
      hideIcon: false,
      hideBadges: false,
      useSlots: false
    }
  )

  // Use the data passed as props
  const documents = computed(() => props.documents || [])
  const processingDocuments = computed(() => props.processingDocuments || [])
  const activeUploads = computed(() => props.activeUploads || [])
  const documentsWithErrors = computed(() => props.documentsWithErrors || [])

  // Derived states
  const hasActiveUploads = computed(() => activeUploads.value.length > 0)
  const hasProcessingDocuments = computed(() => processingDocuments.value.length > 0)
  const hasDocuments = computed(() => documents.value.length > 0)
  const hasCriticalErrors = computed(() => documentsWithErrors.value.length > 0)

  // Combined activity state - uploading OR processing
  const hasActivity = computed(() => hasActiveUploads.value || hasProcessingDocuments.value)

  // Count of active operations
  const uploadCount = computed(() => activeUploads.value.length)
  const processingCount = computed(() => processingDocuments.value.length)
  const totalActiveCount = computed(() => uploadCount.value + processingCount.value)

  // Success message state
  const showSuccessMessage = ref(false)
  let successMessageTimeoutId: number | null = null

  // Track previous activity state for transition detection
  const wasPreviouslyActive = ref(false)

  // Initialize the previous state when component mounts
  onMounted(() => {
    // Small delay to ensure the document module is fully initialized
    setTimeout(() => {
      wasPreviouslyActive.value = hasActivity.value
    }, 500)
  })

  // Watch for activity state changes to show success message
  watch(
    () => hasActivity.value,
    (isActiveNow, wasActiveBefore) => {
      // If we just finished all activity (was true, now false) and have documents without critical errors
      if (
        wasPreviouslyActive.value &&
        !isActiveNow &&
        hasDocuments.value &&
        !hasCriticalErrors.value
      ) {
        showSuccessMessage.value = true

        // Auto-hide after specified time (default 3 seconds)
        const autoHideAfter = 3000 // Default 3 seconds
        if (autoHideAfter > 0) {
          if (successMessageTimeoutId) clearTimeout(successMessageTimeoutId)
          successMessageTimeoutId = window.setTimeout(() => {
            showSuccessMessage.value = false
            successMessageTimeoutId = null
          }, autoHideAfter)
        }
      }

      // Update the previous state
      wasPreviouslyActive.value = isActiveNow
    },
    { immediate: false }
  )

  // Hide success message when new activity starts
  watch(
    () => hasActivity.value,
    (isActiveNow) => {
      if (isActiveNow && showSuccessMessage.value) {
        showSuccessMessage.value = false
        if (successMessageTimeoutId) {
          clearTimeout(successMessageTimeoutId)
          successMessageTimeoutId = null
        }
      }
    }
  )

  // Clean up on unmount
  onBeforeUnmount(() => {
    if (successMessageTimeoutId) clearTimeout(successMessageTimeoutId)
  })

  // Determine when to show the status
  const shouldShowStatus = computed(() => {
    return hasActivity.value || showSuccessMessage.value
  })

  // Get status text based on current activity
  const getStatusText = computed(() => {
    // Use custom template if provided
    if (props.customTemplate && hasActivity.value) {
      return props.customTemplate.replace('{count}', totalActiveCount.value.toString())
    }

    // Default behavior
    if (hasActiveUploads.value && hasProcessingDocuments.value) {
      // Both uploading and processing
      return `${uploadCount.value} ${t('Uploading')}, ${processingCount.value} ${t('ProcessingDocs')}`
    } else if (hasActiveUploads.value) {
      // Only uploading
      return t('DocumentsUploading')
    } else if (hasProcessingDocuments.value) {
      // Only processing
      return t('DocumentsBeingAnalyzed')
    }
    return ''
  })

  // Get status icon and styling
  const statusConfig = computed(() => {
    if (hasActivity.value) {
      return {
        icon: 'loading',
        containerClass: 'border border-purple-100 bg-purple-50 text-purple-700',
        iconClass: 'text-purple-500',
        badgeClass: 'text-green-800 bg-green-100'
      }
    } else {
      return {
        icon: 'success',
        containerClass: 'border border-green-100 bg-green-50 text-green-700',
        iconClass: 'text-green-500',
        badgeClass: 'text-green-800 bg-green-100'
      }
    }
  })

  // Expose methods for parent components that might need them
  const hideSuccessMessage = () => {
    showSuccessMessage.value = false
    if (successMessageTimeoutId) {
      clearTimeout(successMessageTimeoutId)
      successMessageTimeoutId = null
    }
  }

  const forceShowSuccessMessage = () => {
    if (hasDocuments.value && !hasActivity.value && !hasCriticalErrors.value) {
      showSuccessMessage.value = true
    }
  }

  // Expose reactive state for parent components
  defineExpose({
    hasActivity,
    hasDocuments,
    showSuccessMessage,
    hideSuccessMessage,
    forceShowSuccessMessage,
    uploadCount,
    processingCount,
    totalActiveCount
  })
</script>

<template>
  <transition
    name="fade"
    mode="out-in"
    :duration="{ enter: 300, leave: 500 }"
  >
    <div
      v-if="shouldShowStatus"
      key="status-container"
      :class="['flex items-center gap-2', props.compact ? 'mt-1' : 'mt-2']"
    >
      <!-- Slot-based rendering for full customization -->
      <template v-if="props.useSlots">
        <slot
          :hasActivity="hasActivity"
          :hasActiveUploads="hasActiveUploads"
          :hasProcessingDocuments="hasProcessingDocuments"
          :uploadCount="uploadCount"
          :processingCount="processingCount"
          :totalActiveCount="totalActiveCount"
          :statusText="getStatusText"
          :showSuccessMessage="showSuccessMessage"
          :statusConfig="statusConfig"
        >
          <!-- Default slot content if no slot provided -->
          <div class="w-full rounded-md bg-purple-50 px-3 py-2 text-purple-700">
            <span>{{ getStatusText || t('AllDocumentsAnalyzed') }}</span>
          </div>
        </slot>
      </template>

      <!-- Default rendering with customization options -->
      <template v-else>
        <div
          :class="[
            'flex w-full items-center gap-1.5 rounded-md px-3 py-1',
            props.compact ? 'text-xs' : 'text-sm',
            props.customContainerClass || statusConfig.containerClass
          ]"
        >
          <!-- Status icon -->
          <template v-if="!props.hideIcon">
            <Loader2
              v-if="statusConfig.icon === 'loading'"
              :class="[
                'mr-1 shrink-0 animate-spin',
                props.compact ? 'size-3' : 'size-4',
                props.customIconClass || statusConfig.iconClass
              ]"
            />
            <CheckCircle
              v-else
              :class="[
                'mr-1 shrink-0',
                props.compact ? 'size-3' : 'size-4',
                props.customIconClass || statusConfig.iconClass
              ]"
            />
          </template>

          <!-- Status text -->
          <span :class="props.customTextClass">
            <template v-if="hasActivity">{{ getStatusText }}</template>
            <template v-else>{{ t('AllDocumentsAnalyzed') }}</template>
          </span>

          <!-- Activity details for active state -->
          <template v-if="!props.hideBadges">
            <div
              v-if="hasActivity"
              class="ml-auto flex items-center gap-1.5"
            >
              <!-- Upload count badge -->
              <div
                v-if="hasActiveUploads"
                :class="[
                  'rounded bg-blue-100 px-2 py-0.5 text-blue-800',
                  props.compact ? 'text-xs' : 'text-xs'
                ]"
              >
                {{ uploadCount }} {{ t('Uploading') }}
              </div>

              <!-- Processing count badge -->
              <div
                v-if="hasProcessingDocuments"
                :class="[
                  'rounded bg-purple-100 px-2 py-0.5 text-purple-800',
                  props.compact ? 'text-xs' : 'text-xs'
                ]"
              >
                {{ processingCount }} {{ t('Processing') }}
              </div>
            </div>

            <!-- Success state badge -->
            <div
              v-else
              :class="[
                'ml-auto rounded px-2 py-0.5',
                props.compact ? 'text-xs' : 'text-xs',
                statusConfig.badgeClass
              ]"
            >
              {{ t('DocumentsReady') }}
            </div>
          </template>
        </div>
      </template>
    </div>
  </transition>
</template>

<style scoped>
  .fade-enter-active,
  .fade-leave-active {
    transition:
      opacity 0.4s ease,
      transform 0.3s ease;
  }

  .fade-enter-from {
    opacity: 0;
    transform: translateY(-5px);
  }

  .fade-leave-to {
    opacity: 0;
    transform: translateY(5px);
  }

  .fade-enter-to,
  .fade-leave-from {
    opacity: 1;
    transform: translateY(0);
  }
</style>
